import { Spinner, useToast } from "@closer/ui";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { usePendingConsents } from "../pages/auth/LegalConsentFields";
import { useAuth } from "./authContext";
import { erisimKaybiMesaji, resolveSafeWorkspace } from "./safeWorkspace";

/** Oturum zorunlu bölgeler: yoksa /login (geldiği yol state.from'da taşınır). */
export function RequireAuth() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <Outlet />;
}

/**
 * YETKİ GEREKTİREN ROTA KAPISI (CLOS-814).
 *
 * ⚠ Bu bir güvenlik sınırı DEĞİL, bir KAPANMA. Gerçek kapı sunucuda (`requirePermission`)
 * ve o kaldırılmadıkça veri sızmaz. Buradaki kapının işi başka: kart *"eski route/cache
 * ile erişim korunmaz"* ve *"açık sekme/direct URL yeniden doğrulanır"* diyor.
 *
 * Somut senaryo: kullanıcı hukuk bürosundayken Müvekkiller sayfasını açık bırakıyor, sonra
 * başka bir sekmede şirkete geçiyor ya da organizasyonun türü düzeltiliyor. Kapı olmasaydı
 * o sekme açık kalır, sayfa çizilir ve her istek 403 alırdı — kullanıcıya "sistem bozuk"
 * gibi görünen, aslında doğru çalışan bir red. Menüden gizlemek de yetmiyor: gizli bir
 * menü, adresi bilen bir sekmeyi durdurmaz.
 *
 * ⚠ Bağlam YÜKLENİRKEN yönlendirme YAPILMIYOR. Yetki listesi henüz gelmemişken `can()`
 * her şeye `false` der; o anda yönlendirseydik yetkisi TAM olan kullanıcı da her yenilemede
 * dışarı atılırdı.
 */
export function RequirePermission({ permission }: { permission: string }) {
  const { activeContext, can } = useAuth();

  if (!activeContext) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!can(permission)) return <Navigate to="/app" replace />;
  return <Outlet />;
}

/**
 * Doğrulanmamış hesabı doğrulama ekranına yönlendirir (CLOS-359).
 *
 * **Bu bir güvenlik sınırı DEĞİLDİR, bir kolaylıktır.** Gerçek kapı sunucuda
 * (`requireVerifiedEmail`); burası yalnız kullanıcıyı 403 duvarına toslatmak yerine
 * yapabileceği şeye götürür. İkisi karıştırılırsa, istemci kontrolü kaldırıldığında
 * güvenlik de kalkmış sanılır — oysa kalkmaz.
 */
export function RequireVerifiedEmail() {
  const { user } = useAuth();
  if (user && !user.emailVerified) return <Navigate to="/verify-email" replace />;
  return <Outlet />;
}

/**
 * Güncellenen zorunlu metinleri onaylamamış kullanıcıyı yeniden onay ekranına götürür
 * (CLOS-343).
 *
 * `RequireVerifiedEmail` gibi bu da bir KOLAYLIKTIR, güvenlik sınırı değil: gerçek kapı
 * sunucudaki `requireCurrentConsents`. Burası yalnız kullanıcıyı 403 duvarına toslatmak
 * yerine yapabileceği tek şeye götürür.
 *
 * Sorgu sürerken hiçbir yere yönlendirilmez: cevap gelmeden karar vermek, onayı tam olan
 * kullanıcıyı da her yüklemede onay ekranına düşürürdü.
 */
export function RequireCurrentConsents() {
  const { data: eksikler, isLoading } = usePendingConsents();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (eksikler && eksikler.length > 0) return <Navigate to="/consents" replace />;
  return <Outlet />;
}

/**
 * Aktif çalışma alanı zorunlu bölgeler: yoksa çalışma alanı seçim ekranı.
 *
 * ⚠ Yalnız `activeTenantId`'ye bakmak YETMEZ (bug avı, CLOS-496 sınıfı guard açığı):
 * o değer localStorage'dan geliyor ve bir string olduğu için hep truthy. Üyeliği iptal
 * edilmiş ya da başka bir hesapla girilmiş kullanıcının localStorage'ında eski id kalır;
 * yalnız id'ye bakan guard onu `/app`'e sokuyordu (boş alan adı, yanıltıcı dropdown ve
 * sonraki modül çağrılarında 403). Bu yüzden id'nin GERÇEK bir üyeliğe karşılık geldiği
 * de doğrulanıyor.
 *
 * ⚠ VARLIK da yetmez (CLOS-79 `[042]`): üyelik satırı duruyor ama askıya alınmış olabilir.
 * Kart *"son kullanılan alan kimliği tek başına erişim kanıtı değildir; her listelemede
 * güncel üyelik ve erişim durumu yeniden değerlendirilir"* diyor. Bayat seçim aynı
 * CLOS-496 sınıfı hatayı ikinci kez üretirdi: kullanıcı `/app`'e girer, adı görür ve her
 * modül çağrısında 403 alırdı. Karar sunucudan gelen `selectable` alanında — istemci
 * `status === "active"` diye kendi kararını üretmiyor.
 */
export function RequireTenant() {
  const {
    activeTenantId,
    memberships,
    isLoading,
    membershipsLoading,
    recoverWorkspace,
    setActiveTenant,
    user,
  } = useAuth();
  const toast = useToast();

  // Oturum VEYA üyelikler gelene kadar karar verme: erken bakılsaydı GEÇERLİ bir tenant
  // da bir an için "karşılığı yok" görünüp /tenants'a atılırdı (üyelik listesi henüz boş).
  const bekliyor = isLoading || membershipsLoading;
  const karar = bekliyor
    ? null
    : resolveSafeWorkspace({ alanlar: memberships, istenen: activeTenantId });

  /**
   * Güvenli geri dönüş bir YAN ETKİ (CLOS-81 `[044]`): bağlam yazmak ve eski alanın
   * verisini atmak render sırasında yapılamaz. Karar saf fonksiyonda, uygulaması burada.
   */
  useEffect(() => {
    if (karar?.tur !== "yedek") return;
    recoverWorkspace(karar);
    // Kart: "erişim kaybı anlaşılır mesajla açıklanır." Metin saf fonksiyondan geliyor.
    toast.info(erisimKaybiMesaji(karar));
  }, [karar, recoverWorkspace, toast]);

  /**
   * ⚠ KENDİLİĞİNDEN SEÇİLEN ALAN GERÇEKTEN YAZILIYOR — canlı ortamda ölçüldü
   * (5 Eylül 2026).
   *
   * Tek organizasyonu olan kullanıcı için `resolveSafeWorkspace` "geçerli: şu alan" diyor
   * ve guard bu sayede seçim ekranını GÖSTERMİYORDU. Ama kararı kimse bağlama YAZMIYORDU:
   * `activeTenantId` null kalıyor, sayfa "Çalışma alanı seçilmedi" gösteriyor ve modül
   * çağrıları `x-tenant-id` başlığı olmadan gidip *"x-tenant-id başlığı zorunludur"*
   * hatasına düşüyordu. Yani ekran kalkmıştı, seçim kalkmamıştı.
   *
   * ⚠ YAN ETKİ, RENDER DEĞİL: yukarıdaki kurtarma ile aynı gerekçe — bağlam yazmak
   * render sırasında yapılamaz.
   */
  useEffect(() => {
    if (karar?.tur !== "gecerli") return;
    if (activeTenantId === karar.tenantId) return;
    setActiveTenant(karar.tenantId, user?.id ?? null);
  }, [karar, activeTenantId, setActiveTenant, user]);

  if (bekliyor || karar === null) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  /**
   * Yedek alana geçiş yapılırken bir an bekleniyor. `Navigate` atılsaydı kullanıcı önce
   * seçim ekranını görür, sonra kendiliğinden geri dönerdi — kartın istediği "güvenli
   * geri dönüş" değil, bir titreme olurdu.
   */
  if (karar.tur === "yedek") {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Hiç seçim yok ya da gidilecek erişilebilir alan kalmadı → seçim ekranı (`[042]`).
  if (karar.tur === "sec") return <Navigate to="/tenants" replace />;

  return <Outlet />;
}

/**
 * İlk giriş onboarding kapısı (CLOS-224 `[047]`).
 *
 * Kart: *"Onboarding tamamlanmadan hassas organizasyon verisi gösterilmez."* Durum
 * oturum yükünde taşınıyor, yani ilk render'da karar verilebiliyor — ayrı bir istek
 * beklemek her açılışta bir titreme üretirdi.
 *
 * ⚠ `/onboarding` rotası bu guard'ın DIŞINDA; içinde olsaydı sonsuz yönlendirme olurdu.
 *
 * `skipped` de geçerli bir sonuçtur: atlayan kullanıcı bir daha bu ekranla karşılaşmaz.
 */
export function RequireOnboarding() {
  const { user, memberships, isLoading, membershipsLoading } = useAuth();
  /**
   * ⚠ ÜYELİKLER DE BEKLENİYOR — canlı ortamda ölçüldü (5 Eylül 2026).
   *
   * Aşağıdaki koruma `memberships`'e bakıyor ama guard yalnız oturumu bekliyordu. Üyelik
   * listesi ayrı bir istekle geliyor; ilk render'da dizi HENÜZ BOŞ oluyor, `kurumsalUyelik`
   * false çıkıyor ve organizasyonu olan kullanıcı kuruluma gönderiliyordu. Sonuç: her
   * sayfa yenilemesinde ve her girişte onboarding ekranı — düzeltmenin kendisi yarışa
   * yenildiği için görünmüyordu.
   *
   * ⚠ "Ölçemedim" ile "yok" aynı şey değil: liste gelmeden verilen karar, listenin boş
   * olduğunu değil henüz bilinmediğini yansıtır.
   */
  if (isLoading || membershipsLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  /**
   * ⚠ ORGANİZASYON ÜYESİ OLAN KULLANICI KURULUMA GÖNDERİLMİYOR — saha bulgusu
   * (4 Eylül 2026).
   *
   * `onboardingStatus` yalnız bu ekran tamamlanınca değişiyor. Davetle bir organizasyona
   * katılan kullanıcı o ekrandan hiç geçmiyor: kaydoluyor, daveti kabul ediyor, giriş
   * yapıyor ve ürün ona *"organizasyon oluşturun"* diyor — zaten bir organizasyonun
   * üyesiyken. Ekran bunu kendi de yazıyordu ("Ayrıca 1 organizasyona erişiminiz var")
   * ve yine ilk kurulum gibi davranıyordu.
   *
   * Kartın koruması (CLOS-224: *"onboarding tamamlanmadan hassas organizasyon verisi
   * gösterilmez"*) ilk kez giren, hiçbir yere bağlı olmayan kullanıcı için. Daveti
   * kabul etmiş biri o kullanıcı değil: bağlamı bilerek seçmiş.
   */
  const kurumsalUyelik = memberships.some((uyelik) => uyelik.type !== "individual");
  if (user && user.onboardingStatus === "pending" && !kurumsalUyelik) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}
